import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ParentShell } from '../components/ParentShell'
import { parentApi, type ChildSummary } from '../api/parent'
import { queryKeys } from '../api/queryKeys'

/** Главная страница родителя — список «моих детей» со сводкой. */
export function ParentDashboard() {
  const { t } = useTranslation()
  const children = useQuery({
    queryKey: queryKeys.parent.children(),
    queryFn: parentApi.myChildren,
    retry: 1,
  })

  return (
    <ParentShell title={t('parentDashboard.title')} subtitle={t('parentDashboard.subtitle')}>
      {children.isLoading && <div className="text-center py-10 text-gray-500">{t('common.loading')}</div>}

      {children.isError && (
        <div className="bg-red-50 border border-red-200 text-red-900 rounded-lg p-4">
          {t('parentDashboard.loadError')}
        </div>
      )}

      {children.data?.length === 0 && (
        <div className="text-center py-12 bg-gradient-to-br from-primary-soft/30 to-white border border-purple-100/80 rounded-2xl">
          <div className="text-5xl mb-3">👨‍👧</div>
          <h3 className="font-semibold text-gray-900 mb-2">{t('parentDashboard.emptyTitle')}</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto px-4">
            {t('parentDashboard.emptyText')}
          </p>
        </div>
      )}

      {children.data && children.data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {children.data.map((c) => <ChildCard key={c.studentId} child={c} t={t} />)}
        </div>
      )}
    </ParentShell>
  )
}

function ChildCard({ child, t }: { child: ChildSummary; t: TFunction }) {
  return (
    <Link
      to={`/parent/child/${child.studentId}`}
      className="group block bg-white border border-purple-100/80 rounded-2xl p-5 transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_12px_32px_-8px_rgba(87,10,166,0.25)]"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-purple-600 text-white text-lg font-bold flex items-center justify-center flex-shrink-0">
          {child.fullName.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 group-hover:text-primary truncate">
            {child.fullName}
          </h3>
          <p className="text-xs text-gray-500">
            {child.className
              ? t('parentDashboard.classWithGrade', { className: child.className, grade: child.gradeLevel ?? '?' })
              : t('parentDashboard.classNotSet')}
            <span className="ml-2">· {relationshipLabel(child.relationship, t)}</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <Metric
          label={t('parentDashboard.metricAvg')}
          value={child.avgGrade != null ? child.avgGrade.toFixed(2) : '—'}
          color={child.avgGrade != null && child.avgGrade >= 4 ? 'green' : child.avgGrade != null && child.avgGrade >= 3 ? 'yellow' : 'red'}
        />
        <Metric label={t('parentDashboard.metricGrades')} value={child.gradesCount.toString()} color="blue" />
        <Metric
          label={t('parentDashboard.metricAttendance')}
          value={child.attendancePercent != null ? `${child.attendancePercent}%` : '—'}
          color={child.attendancePercent != null && child.attendancePercent >= 90 ? 'green' : 'yellow'}
        />
      </div>
    </Link>
  )
}

function Metric({ label, value, color }: { label: string; value: string; color: 'green' | 'yellow' | 'red' | 'blue' }) {
  const map = {
    green: 'bg-green-50 text-green-800 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
  }
  return (
    <div className={`${map[color]} border rounded-lg p-2`}>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-75">{label}</div>
    </div>
  )
}

function relationshipLabel(r: string, t: TFunction): string {
  return {
    mother: t('parentDashboard.relationshipMother'),
    father: t('parentDashboard.relationshipFather'),
    guardian: t('parentDashboard.relationshipGuardian'),
    parent: t('parentDashboard.relationshipParent'),
  }[r] ?? r
}
